import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ReplyItemComponent } from '../reply-item/reply-item.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CommunityService } from '../../../services/community.service';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { TimeAgoOrDatePipe } from '../../pipe/time-ago-or-date.pipe';
import { fadeStagger, slideIn } from '../../app.animations';

import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { allCountries } from 'country-telephone-data';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

type LinkPreview = {
  title: string | null;
  url: string;
  source: string | null;
  image_url?: string | null;
};
type LinkPreviewState = {
  loading: boolean;
  data?: LinkPreview | null;
  error?: string | null;
};

type ShareTarget = {
  key: string;
  label: string;
  href?: string;
  iconClass?: string;
};

@Component({
  selector: 'app-post-item',
  standalone: true,
  templateUrl: './post-item.component.html',
  styleUrls: ['./post-item.component.css'],
  imports: [
    CommonModule,
    ReplyItemComponent,
    FormsModule,
    ReactiveFormsModule,
    NgxDocViewerModule,
    TimeAgoOrDatePipe,
  ],
  animations: [fadeStagger, slideIn],
})
export class PostItemComponent implements OnInit {
  @Input() post: any;
  @Input() isMobile = false;
  @Input() activeReplyPostId: number | null = null;
  @Input() communityID!: number;
  @Input() profileModalTemplate!: TemplateRef<any>;
  @Input() submittingReplyPostId: number | null = null;
  @Input() currentUserId!: number;
  @Input() availabiltyDurations: Array<{ duration_id: number; value: string }> = [];

  MobileCountries = allCountries.map(c => ({
    name: c.name,
    dialCode: c.dialCode,
    iso2: c.iso2
  }));

  @Input() isRecruiter = false;

  @Input() spaceID!: number;

  @Output() toggleReply = new EventEmitter<{
    replyId: number;
    replyDepth: number;
    parentPostId: number;
    onDone?: () => void;
  }>();
  @Output() submitReply = new EventEmitter<{
    parentId: number;
    replyDepth: number;
    content: string;
    files: File[];
  }>();
  @Output() deletePost = new EventEmitter<number>();

  // UI state
  expanded = false;
  replying = false;
  replyContent = '';
  selectedImages: File[] = [];
  imagePreviews: string[] = [];
  imageError = '';
  selectedUser: any;
  isLoadingReplies = false;
  isGeneratingPreview = false;

  @ViewChild('profileModal') profileModal!: TemplateRef<any>;
  @ViewChild('imageViewerModal') imageViewerModal!: TemplateRef<any>;
  currentImageUrl: string = '';
  currentImageIndex: number = 0;
  viewerImages: string[] = [];
  viewerSource: 'attachments' | 'previews' = 'attachments';
  private touchStartX = 0;
  private touchEndX = 0;

  @ViewChild('editPostModal') editPostModal: any;
  editedContent: string = '';
  existingAttachments: any[] = [];
  newEditImages: File[] = [];
  editImagePreviews: string[] = [];
  @ViewChild('editUploadInput') editUploadInput!: any;

  @ViewChild('preApplyModal') preApplyModal!: TemplateRef<any>;
  @ViewChild('applyConfirmModal') applyConfirmModal!: TemplateRef<any>;

  @ViewChild('shareModal') shareModal!: TemplateRef<any>;
  shareHeading: string = 'Found this and thought of you';
  shareTargets: ShareTarget[] = [];
  composedMessage = '';

  preApply = {
    checking: false,
    phoneOk: true,
    cvOk: true,
    availabilityOk: true,
    availabilityUpdatedAt: null as string | null,
    availabilityTooltip: '',
  };

  applyMessage: string = '';

  phoneForm: FormGroup;

  countryCode = '';
  phone = '';
  savingPhone = false;

  resumeFile: File | null = null;
  resumeFileName = '';
  savingResume = false;

  availMode: 'now' | 'date' = 'now';
  availStartAt: string | null = null;
  availDurationId: number | null = null;
  savingAvailability = false;
  currentSlug = '';
  currentCommunityId = '';
  private urlRegex = /(https?:\/\/[^\s]+)/i;

  copyOk = false;

  waUrl = '';
  fbUrl = '';
  tgUrl = '';
  xUrl = '';
  liUrl = '';
  mailtoUrl = '';
  smsUrl = '';
  lineUrl = '';

  linkPreview: Record<number, LinkPreviewState> = {};

  constructor(
    private communityService: CommunityService,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private cdRef: ChangeDetectorRef,
    private dataService: FreelancerDataService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.phoneForm = this.fb.group({
      country_code: [''],
      phone: [''],
    });
  }

  get isNews(): boolean {
    const byNewsUrl = !!this.post?.news_url;                      
    const byNewsObj = !!this.post?.news?.url;
    const byType = (this.post?.post_type || '').toLowerCase() === 'news';
    const byFlag = !!this.post?.is_news;
    return byNewsUrl || byNewsObj || byType || byFlag;
  }


  getNewsUrl(): string | null {
    return this.post?.news_url || this.post?.news?.url || null;
  }


  getNewsSourceLabel(): string {
    const src = this.post?.news_source || this.post?.news?.source;
    if (src) return src;
    const url = this.getNewsUrl();
    const domain = url ? this.domainOf(url) : '';
    return domain || 'News';
  }


  getNewsFaviconDomain(): string {
    const url = this.getNewsUrl();
    return url ? this.domainOf(url) : '';
  }


  getNewsPublishedAt(): string | Date | undefined {
    return (
      this.post?.news_published_at ||
      this.post?.news?.published_at ||
      this.post?.created_at ||
      undefined
    );
  }

 
 getAuthorName(): string {
  const js = this.post?.Users?.Jobseekers;
  const hasJs = Array.isArray(js) && js.length > 0;
  const jsName = hasJs ? (js[0]?.name || '').trim() : '';

  if (jsName) return jsName;

  if (this.isNews && !hasJs) {
    return 'News From Komudu';
  }

  return 'Anonymous';
}


  getAuthorAvatar(): string {
    const jsAvatar = this.post?.Users?.Jobseekers?.[0]?.avatar_url;
    if (jsAvatar) return jsAvatar;

    return this.isNews
      ? 'logo/KA_news.png'
      : 'https://kontact-app-public.s3-ap-southeast-1.amazonaws.com/images/Sign-Up-Image.jpg';
  }



  ngOnInit(): void {
  if (!this.spaceID || Number.isNaN(this.spaceID)) {
    const ls = localStorage.getItem('spaceID');
    const parsed = ls ? parseInt(ls, 10) : 0;
    if (parsed > 0) this.spaceID = parsed;
  }

  if (this.isNews || !this.hasLink(this.post?.content)) return;

  const targetId = `post-${(this.post?.id ?? this.post?.uuid) || ''}`;
  const el = typeof window !== 'undefined' ? document.getElementById(targetId) : null;

  const loadPreview = () => this.ensureLinkPreview();

  if (typeof window !== 'undefined' && 'IntersectionObserver' in window && el) {
    const io = new IntersectionObserver((entries, obs) => {
      if (entries.some(e => e.isIntersecting)) {
        loadPreview();
        obs.disconnect();
      }
    }, { rootMargin: '200px 0px' });
    io.observe(el);
  } else {
    loadPreview();
  }
}




  openShareModal(): void {
    if (!this.post?.uuid) {
      this.toastr.error('Share unavailable: missing post id.');
      return;
    }
    this.shareHeading = this.shareHeading?.trim() || 'Found this and thought of you';
    this.composedMessage = this.buildComposedText(this.shareHeading);
    this.shareTargets = this.buildShareTargets();
    this.computeShareUrls();
    this.modalService.open(this.shareModal, { centered: true });
  }

  onShareHeadingInput(): void {
    this.composedMessage = this.buildComposedText(this.shareHeading);
    this.shareTargets = this.buildShareTargets();
    this.computeShareUrls();
  }

  private buildComposedText(heading: string): string {
    const title = this.getShareTitle();
    const link = this.getPostPublicUrl();
    return `${(heading || '').trim()}: ${title}\n${link}`;
  }

  private getSlugAndCommunityId(): { slug: string; communityId: string } {
    let slug = localStorage.getItem('slug') || '';
    let communityId = localStorage.getItem('communityId') || (this.communityID ? String(this.communityID) : '');

    if ((!slug || !communityId) && typeof window !== 'undefined') {
      try {
        const parts = window.location.pathname.split('/').filter(Boolean);
        if (parts.length >= 2) {
          if (!slug) slug = parts[0];
          if (!communityId) communityId = parts[1];
        }
      } catch {}
    }
    return { slug, communityId };
  }

  private getPostPublicUrl(): string {
    const { slug, communityId } = this.getSlugAndCommunityId();
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const base = (environment as any).publicBaseUrl || origin || 'https://your-public-domain.com';
    const uuid = this.post?.uuid;
    return `${base.replace(/\/$/, '')}/${slug}/${communityId}/post/${uuid}`;
  }

  private getShareTitle(): string {
    if (this.isNews) {
      return (this.post?.news?.title || this.post?.content || '').split('\n')[0].trim();
    }
    if ((this.post?.post_type || '').toLowerCase() === 'job') {
      const j = this.parseJob(this.post?.content);
      const jobTitle = (j.title || '').trim();
      if (jobTitle) return jobTitle;
    }
    return (this.post?.content || '').split('\n')[0].trim();
  }

  private enc(s: string): string { return encodeURIComponent(s); }

  private buildShareTargets(): ShareTarget[] {
    const url = this.getPostPublicUrl();
    const title = this.getShareTitle();
    const heading = (this.shareHeading || '').trim();
    const message = `${heading}: ${title}`;
    const full = `${message}\n${url}`;

    return [
      { key: 'native',   label: 'Share…',        iconClass: 'bi-share' },
      { key: 'whatsapp', label: 'WhatsApp',      href: `https://wa.me/?text=${this.enc(full)}`,                                    iconClass: 'bi-whatsapp' },
      { key: 'telegram', label: 'Telegram',      href: `https://t.me/share/url?url=${this.enc(url)}&text=${this.enc(message)}`,    iconClass: 'bi-telegram' },
      { key: 'facebook', label: 'Facebook',      href: `https://www.facebook.com/sharer/sharer.php?u=${this.enc(url)}&quote=${this.enc(message)}`, iconClass: 'bi-facebook' },
      { key: 'linkedin', label: 'LinkedIn',      href: `https://www.linkedin.com/sharing/share-offsite/?url=${this.enc(url)}`,     iconClass: 'bi-linkedin' },
      { key: 'twitter',  label: 'X / Twitter',   href: `https://twitter.com/intent/tweet?text=${this.enc(message)}&url=${this.enc(url)}`, iconClass: 'bi-twitter-x' },
      { key: 'email',    label: 'Email',         href: `mailto:?subject=${this.enc(title)}&body=${this.enc(full)}`,                iconClass: 'bi-envelope' },
      { key: 'sms',      label: 'SMS',           href: `sms:?&body=${this.enc(full)}`,                                             iconClass: 'bi-chat-dots' },
      { key: 'line',     label: 'LINE',          href: `https://social-plugins.line.me/lineit/share?url=${this.enc(url)}`,         iconClass: 'bi-chat-left-text' },
      { key: 'copy',     label: 'Copy link',     iconClass: 'bi-clipboard' },
    ];
  }

  private computeShareUrls(): void {
    const url = this.getPostPublicUrl();
    const title = this.getShareTitle();
    const heading = (this.shareHeading || '').trim();
    const message = `${heading}: ${title}`;
    const full = `${message}\n${url}`;

    this.waUrl     = `https://wa.me/?text=${this.enc(full)}`;
    this.tgUrl     = `https://t.me/share/url?url=${this.enc(url)}&text=${this.enc(message)}`;
    this.fbUrl     = `https://www.facebook.com/sharer/sharer.php?u=${this.enc(url)}&quote=${this.enc(message)}`;
    this.liUrl     = `https://www.linkedin.com/sharing/share-offsite/?url=${this.enc(url)}`;
    this.xUrl      = `https://x.com/intent/tweet?text=${this.enc(message)}&url=${this.enc(url)}`;
    this.mailtoUrl = `mailto:?subject=${this.enc(title)}&body=${this.enc(full)}`;
    this.smsUrl    = this.buildSmsHref(full);
    this.lineUrl   = `https://social-plugins.line.me/lineit/share?url=${this.enc(url)}`;
  }

  nativeShare(): void { this.shareNative(); }
  copyLink(): void {
    this.copyShareText();
    this.copyOk = true;
    setTimeout(() => (this.copyOk = false), 2000);
  }

  shareNative(): void {
    const url = this.getPostPublicUrl();
    const title = this.getShareTitle();
    const text = `${(this.shareHeading || '').trim()}: ${title}`;

    if ((navigator as any)?.share) {
      (navigator as any).share({ title, text, url }).catch(() => {});
    } else {
      this.toastr.info('Native share is not supported on this browser.');
    }
  }

  openShareTarget(target: ShareTarget): void {
    if (target.key === 'native') { this.shareNative(); return; }
    if (target.key === 'copy')   { this.copyShareText(); return; }
    if (target.href) { window.open(target.href, '_blank', 'noopener,noreferrer'); }
  }

  async copyShareText(): Promise<void> {
    const text = this.buildComposedText(this.shareHeading);

    try {
      if (window.isSecureContext && 'clipboard' in navigator) {
        const supportsClipboardItem =
          typeof (window as any).ClipboardItem !== 'undefined' &&
          typeof (navigator as any).clipboard.write === 'function';

        if (supportsClipboardItem) {
          const ClipboardItemCtor = (window as any).ClipboardItem;
          const blob = new Blob([text], { type: 'text/plain' });
          await (navigator as any).clipboard.write([new ClipboardItemCtor({ 'text/plain': blob })]);
        } else {
          await navigator.clipboard.writeText(text);
        }
        this.toastr.success('Copied to clipboard');
        return;
      }
    } catch {}

    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', 'true');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);

      const ok = document.execCommand('copy');
      document.body.removeChild(ta);

      if (ok) this.toastr.success('Copied to clipboard');
      else this.toastr.error('Could not copy');
    } catch {
      this.toastr.error('Could not copy');
    }
  }


  hasLink(s?: string): boolean { return !!s && this.urlRegex.test(s); }
  firstUrl(s?: string): string | null { if (!s) return null; const m = s.match(this.urlRegex); return m ? m[0] : null; }
  domainOf(u?: string | null): string {
    if (!u) return '';
    try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
  }

  private firstUrlRe = /(https?:\/\/[^\s]+|www\.[^\s]+)$/im;

  splitContentAndFirstUrl(text?: string): { content: string; url: string | null } {
    const s = (text || '').trim();
    if (!s) return { content: '', url: null };
    const m = s.match(this.firstUrlRe);
    if (m) {
      const url = m[0].startsWith('http') ? m[0] : `https://${m[0]}`;
      const content = s.replace(m[0], '').replace(/\s+\n/g, '\n').trim();
      return { content, url };
    }
    return { content: s, url: null };
  }

  stripProtocol(u: string): string { return u.replace(/^https?:\/\//i, '').replace(/^www\./i, ''); }


  parseJob(content: string | null | undefined) {
    const lines = (content || '')
      .split(/\r?\n/).map(l => l.trim()).filter(Boolean);

    const pick = (keys: string[]) => {
      const idx = lines.findIndex(l => keys.some(k => l.toLowerCase().startsWith(k)));
      if (idx === -1) return '';
      const parts = lines[idx].split(':'); parts.shift();
      return parts.join(':').trim();
    };

    const title = pick(['job title', 'designation', 'title']);
    const description = pick(['description', 'desc', 'job scope', 'scope']);
    const location = pick(['location', 'loc']);
    const startDate = pick(['start date', 'start']);
    const duration = pick(['duration', 'notice']);

    return { title, description, location, startDate, duration };
  }

  toggleExpand(): void { this.expanded = !this.expanded; }
  get isOwner(): boolean { return !!(this.post?.user_id && this.currentUserId === this.post.user_id); }

  onToggleReplies(): void {
    if (this.post?.post_type === 'job' && !this.isOwner) return;

    this.post.showReplies = !this.post.showReplies;
    if (this.post.showReplies) {
      this.isLoadingReplies = true;
      this.toggleReply.emit({
        replyId: this.post.id,
        replyDepth: this.post.reply_depth,
        parentPostId: this.post.id,
        onDone: () => (this.isLoadingReplies = false),
      });
    }
  }


  onClickApply(): void {
    if (this.post?.post_type !== 'job') return;
    if (this.isOwner) return;
    if (this.isRecruiter) return;
    this.runPreApplyCheck();
  }

  private runPreApplyCheck(): void {
    this.preApply.checking = true;

    const headerValue = this.spaceID;

    const availability$ = this.dataService.getUserAvailabilty({}, headerValue).pipe(catchError(() => of(null)));
    const profile$      = this.dataService.getFreelancerProfile({}, headerValue).pipe(catchError(() => of(null)));
    const resume$       = this.dataService.getFreelancerResume({}, headerValue).pipe(catchError(() => of(null)));

    forkJoin({ availability: availability$, profile: profile$, resume: resume$ })
      .subscribe({
        next: ({ availability, profile, resume }) => {
          const avRoot = availability?.data ?? availability ?? null;
          const av = avRoot?.availability ?? avRoot ?? null;
          const updatedAt: string | null = av?.updatedAt ?? av?.updated_at ?? null;
          const avCheck = this.computeAvailabilityStatus(updatedAt);
          this.preApply.availabilityOk = avCheck.ok;
          this.preApply.availabilityUpdatedAt = updatedAt;
          this.preApply.availabilityTooltip = avCheck.tooltip;

          if (av?.start_at || av?.duration_id) {
            const start = av?.start_at ? String(av.start_at).split('T')[0] : null;
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const startDate = av?.start_at ? new Date(av.start_at) : null;
            const isNow = !startDate || (startDate.setHours(0, 0, 0, 0), startDate <= today);
            this.availMode = isNow ? 'now' : 'date';
            this.availStartAt = isNow ? null : start;
            this.availDurationId = isNow ? (av?.duration_id ?? null) : null;
          } else {
            this.availMode = 'now';
            this.availStartAt = null;
            this.availDurationId = null;
          }

          const p = profile?.data ?? profile ?? {};
          const rawPhone = (p?.phone ?? '').toString().trim();
          this.preApply.phoneOk = rawPhone.length > 0;

          const initialDial = (p?.country_code ?? '').toString().replace(/^\+/, '');
          this.phoneForm.patchValue({
            country_code: initialDial || '',
            phone: rawPhone,
          });
          this.countryCode = p?.country_code ?? '';
          this.phone = rawPhone;

          const r = resume?.data ?? resume ?? {};
          const resumeName: string = r?.resumeName ?? r?.name ?? '';
          this.preApply.cvOk = !!resumeName;

          this.preApply.checking = false;

          if (this.preApply.phoneOk && this.preApply.cvOk && this.preApply.availabilityOk) {
            this.openApplyConfirm();
          } else {
            this.modalService.open(this.preApplyModal, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
          }
        },
        error: () => {
          this.preApply.checking = false;
          this.modalService.open(this.preApplyModal, { size: 'lg', centered: true, backdrop: 'static', keyboard: false });
        }
      });
  }

  private openApplyConfirm(): void {
    this.applyMessage = '';
    this.modalService.open(this.applyConfirmModal, { centered: true, backdrop: 'static', keyboard: false });
  }

  private computeAvailabilityStatus(updatedAt: string | null): { ok: boolean; days: number; tooltip: string } {
    if (!updatedAt) return { ok: false, days: Infinity, tooltip: 'No availability on file' };
    const days = Math.max(0, Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86_400_000));
    return { ok: days <= 14, days, tooltip: `Last updated ${days} day(s) ago` };
  }


  onEdit(): void {
    this.editedContent = this.post.content || '';
    this.existingAttachments = [...(this.post.CommunityPostAttachment || [])];
    this.newEditImages = [];
    this.editImagePreviews = [];

    this.editedHasLinks = this.isJob() && this.hasUrlOrEmail(this.editedContent);

    this.modalService.open(this.editPostModal, { centered: true });
  }

  onEditedContentInput(): void {
    this.editedHasLinks = this.isJob() && this.hasUrlOrEmail(this.editedContent);
  }

  removeExistingAttachment(index: number): void { this.existingAttachments.splice(index, 1); }

  confirmEdit(modalRef: any): void {
    const content = (this.editedContent || '').trim();

    if (this.isJob() && this.hasUrlOrEmail(content)) {
      this.toastr.error('Job posts cannot include links or email addresses.');
      return;
    }

    if (!content && this.existingAttachments.length === 0 && this.newEditImages.length === 0) {
      this.toastr.error('Nothing to save.');
      return;
    }

    if (content.length > 1000) {
      this.toastr.error(`Content is ${content.length} chars (max 1000). Please shorten your text.`);
      return;
    }

    const fd = new FormData();
    fd.append('content', content);
    if (this.existingAttachments?.length) this.existingAttachments.forEach((att) => fd.append('existing_attachments', att.id.toString()));
    if (this.newEditImages?.length) this.newEditImages.forEach(file => fd.append('files', file));

    this.communityService.updatePost(this.communityID, this.post.id, fd).subscribe({
      next: (res) => {
        const updatedPost = (res as any).data?.data;
        this.post.content = content;
        this.post.CommunityPostAttachment = updatedPost?.CommunityPostAttachment || [];
        this.toastr.success('Post updated successfully');
        this.cdRef.detectChanges();
        modalRef.close();
      },
      error: () => { this.toastr.error('Failed to update post'); }
    });
  }

  onDelete(): void {
    const confirmed = confirm('Are you sure you want to delete this post?');
    if (confirmed) this.deletePost.emit(this.post.id);
  }

  // ---------- AVAILABILITY ----------
  get needsPhone(): boolean { return !this.preApply.phoneOk; }
  get needsCv(): boolean { return !this.preApply.cvOk; }
  get needsAvailability(): boolean { return !this.preApply.availabilityOk; }
  get missingCount(): number {
    return (this.needsPhone ? 1 : 0) + (this.needsCv ? 1 : 0) + (this.needsAvailability ? 1 : 0);
  }

  private urlOrEmailRe =
    /(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}\b)/i;

  editedHasLinks = false;

  private isJob(): boolean { return (this.post?.post_type || '').toLowerCase() === 'job'; }

  private hasUrlOrEmail(t: string): boolean { return this.urlOrEmailRe.test((t || '').toString()); }

  getDurationLabel(id: number | null): string {
    if (!id || id === 8) return '';
    const found = this.availabiltyDurations?.find(d => d.duration_id === id);
    return found ? found.value : '';
  }

  getAvailabilityStatus(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = av?.start_at ? new Date(av.start_at) : null;
    if (start) start.setHours(0, 0, 0, 0);

    const durationLabel = av?.duration_id ? this.getDurationLabel(av.duration_id) : '';
    const show = durationLabel && durationLabel !== 'Unknown' ? durationLabel : null;

    if (!start || start <= today) {
      return show ? `Available Now (${show})` : 'Available Now';
    }
    const formatted = start.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    return show ? `Available From ${formatted} (${show})` : `Available From ${formatted}`;
  }

  getAvailabilityBadgeClass(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const startAt = av?.start_at ? new Date(av.start_at) : null;
    if (startAt) startAt.setHours(0, 0, 0, 0);
    if (!startAt || startAt <= today) return 'bg-success text-white';
    return 'bg-warning text-dark';
  }

  // ---------- PROFILE ----------
  openProfile(userId: number) {
    // allow opening even for news now
    if (!userId) return;
    this.communityService.getUserPublicProfile(this.communityID, userId).subscribe({
      next: (res) => {
        this.selectedUser = (res as any).data.data;
        this.modalService.open(this.profileModal, { size: 'lg', backdrop: 'static', keyboard: false });
      },
      error: () => { this.toastr.error('Failed to load user profile.'); },
    });
  }

  // ---------- APPLICANTS NAV ----------
  goToApplicantList(): void {
    const slug = localStorage.getItem('slug') || '';
    const communityId = localStorage.getItem('communityId') || '';
    const postId = this.post?.id;

    if (!slug || !communityId || !postId) {
      this.toastr.error('Missing slug, community, or post id.');
      return;
    }

    this.router.navigate([`/${slug}/${communityId}/${postId}/applicants`]);
  }
private previewDenylist: RegExp[] = [
  /(^|\.)medium\.com$/i,
  /(^|\.)facebook\.com$/i,
  /(^|\.)x\.com$/i
];
  // ---------- LINK PREVIEW ----------
  private ensureLinkPreview(): void {
  const id = this.post?.id ?? this.post?.uuid;
  if (!id) return;

  const url = this.firstUrl(this.post?.content || '');
  if (!url) return;

  const host = this.domainOf(url);
  if (host && this.previewDenylist.some(rx => rx.test(host))) {
    this.linkPreview[id] = { loading: false, data: null, error: 'denied-host' };
    this.cdRef.markForCheck();
    return;
  }

  if (this.linkPreview[id]?.loading || this.linkPreview[id]?.data) return;

  this.linkPreview[id] = { loading: true };
  this.cdRef.markForCheck();

  this.communityService.getLinkPreviewCached(url).subscribe((res) => {
    if (!res) {
      this.linkPreview[id] = { loading: false, data: null, error: 'no-preview' };
    } else {
      this.linkPreview[id] = {
        loading: false,
        data: {
          title: res.title ?? null,
          url: res.url ?? url,
          source: res.source ?? this.domainOf(res.url ?? url),
          image_url: res.image_url ?? null,
        }
      };
    }
    this.cdRef.detectChanges();
  });
}

  private buildSmsHref(full: string): string {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isIOS ? `sms:&body=${this.enc(full)}` : `sms:?body=${this.enc(full)}`;
  }

  // ---------- IMAGE UPLOADS / REPLIES ----------
  submitPostReply(): void {
    const hasText = this.replyContent.trim().length > 0;
    const hasImages = this.selectedImages.length > 0;
    if (!hasText && !hasImages) return;

    this.submitReply.emit({
      parentId: this.post.id,
      replyDepth: this.post.reply_depth,
      content: this.replyContent,
      files: this.selectedImages,
    });

    this.replyContent = '';
    this.replying = false;
    this.selectedImages = [];
    this.imagePreviews = [];
    this.imageError = '';
    this.isGeneratingPreview = false;
  }

  onFileChange(event: any): void {
    const files = Array.from(event.target.files) as File[];
    const availableSlots = 5 - this.selectedImages.length;

    if (files.length > availableSlots) {
      this.imageError = `You can upload only ${availableSlots} more image(s).`;
      return;
    }

    this.isGeneratingPreview = true;
    let processed = 0;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        this.imageError = `${file.name} is not an image.`;
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 4096 || img.height > 4096) {
            this.imageError = `${file.name} exceeds max resolution`;
            return;
          }

          const finalize = (finalFile: File) => {
            this.selectedImages.push(finalFile);
            this.createPreview(finalFile);
            processed++;
            if (processed === files.length) {
              this.isGeneratingPreview = false;
              this.cdRef.detectChanges();
            }
          };

          if (img.width > 1024) {
            const canvas = document.createElement('canvas');
            const ratio = img.height / img.width;
            canvas.width = 1024;
            canvas.height = 1024 * ratio;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, { type: file.type });
                finalize(resizedFile);
              }
            }, file.type);
          } else {
            finalize(file);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    event.target.value = '';
  }

  createPreview(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreviews.push(e.target.result);
      this.cdRef.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeReplyImage(index: number): void {
    this.selectedImages.splice(index, 1);
    this.imagePreviews.splice(index, 1);
  }

  sanitizeAndLinkify(content: string, limit?: number): string {
    const truncated = limit ? content.slice(0, limit) : content;
    return truncated.replace(
      /(https?:\/\/[^\s]+)/g,
      (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    );
  }

  // ---------- IMAGE VIEWER ----------
  openImageViewer(source: 'attachments' | 'previews', index: number): void {
    this.viewerSource = source;
    this.currentImageIndex = index;

    this.viewerImages = source === 'attachments'
      ? this.post.CommunityPostAttachment.map((a: any) => a.file_url || a.thumbnail_url)
      : [...this.imagePreviews];

    this.currentImageUrl = this.viewerImages[this.currentImageIndex];
    this.modalService.open(this.imageViewerModal, { size: 'lg', centered: true });
  }

  nextImage(): void { if (this.hasNext()) { this.currentImageIndex++; this.currentImageUrl = this.viewerImages[this.currentImageIndex]; } }
  prevImage(): void { if (this.hasPrev()) { this.currentImageIndex--; this.currentImageUrl = this.viewerImages[this.currentImageIndex]; } }
  hasNext(): boolean { return this.currentImageIndex < this.viewerImages.length - 1; }
  hasPrev(): boolean { return this.currentImageIndex > 0; }

  onTouchStart(event: TouchEvent): void { this.touchStartX = event.changedTouches[0].screenX; }
  onTouchMove(event: TouchEvent): void { this.touchEndX = event.changedTouches[0].screenX; }
  onTouchEnd(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    if (Math.abs(deltaX) < 50) return;
    if (deltaX > 50 && this.hasPrev()) this.prevImage();
    else if (deltaX < -50 && this.hasNext()) this.nextImage();
  }


  private ensureSpaceId(): boolean {
    if (this.spaceID && !Number.isNaN(this.spaceID)) return true;
    const ls = localStorage.getItem('spaceID');
    const parsed = ls ? parseInt(ls, 10) : 0;
    if (parsed > 0) { this.spaceID = parsed; return true; }
    this.toastr.error('Cannot determine spaceID. Please reload.');
    return false;
  }

  savePhoneInline(): void {
    if (!this.ensureSpaceId()) return;
    const headerValue = this.spaceID;

    const dialRaw = (this.phoneForm.value.country_code || '').toString().trim();
    const dialCode = dialRaw ? `+${dialRaw.replace(/^\+/, '')}` : '';
    let national = (this.phoneForm.value.phone || '').toString().replace(/\D/g, '');

    const stripZeroDialCodes = ['60', '880', '91', '92', '234'];
    if (national.startsWith('0') && stripZeroDialCodes.includes(dialRaw.replace(/^\+/, ''))) {
      national = national.substring(1);
    }
    if (!national) { this.toastr.error('Phone number is required'); return; }

    this.savingPhone = true;
    const payload = { country_code: dialCode, phone: national };

    this.dataService.updateProfileInfo(payload, headerValue).subscribe({
      next: () => {
        this.savingPhone = false;
        this.preApply.phoneOk = true;
        this.toastr.success('Phone updated');
      },
      error: (err) => {
        this.savingPhone = false;
        this.toastr.error(err?.error?.error?.description || 'Failed to update phone');
      }
    });
  }

  onResumeFileChange(e: Event): void {
    const input = (e.target as HTMLInputElement);
    const file = input?.files?.[0];
    if (!file) return;

    const okType = /pdf|msword|officedocument/.test(file.type) || /\.(pdf|doc|docx)$/i.test(file.name);
    if (!okType) { this.toastr.error('Upload a PDF or Word document'); input.value = ''; return; }
    if (file.size > 5 * 1024 * 1024) { this.toastr.error('Max size 5MB'); input.value = ''; return; }

    this.resumeFile = file;
    this.resumeFileName = file.name;
  }

  uploadResumeInline(): void {
    if (!this.ensureSpaceId()) return;
    if (!this.resumeFile) { this.toastr.error('Choose a CV file first'); return; }
    const headerValue = this.spaceID;

    this.savingResume = true;
    const fd = new FormData();
    fd.append('resume', this.resumeFile);

    this.dataService.uploadResume(fd, headerValue).subscribe({
      next: (res: any) => {
        this.savingResume = false;
        this.preApply.cvOk = true;
        this.resumeFile = null;
        this.resumeFileName = '';
        this.toastr.success(res?.data || 'CV uploaded');
      },
      error: (err) => {
        this.savingResume = false;
        this.toastr.error(err?.error?.error?.description || 'Failed to upload CV');
      }
    });
  }

  saveAvailabilityInline(): void {
    if (!this.ensureSpaceId()) return;
    const headerValue = this.spaceID;

    if (this.availMode === 'now') {
      if (!this.availDurationId) { this.toastr.error('Select a notice period'); return; }
    } else {
      if (!this.availStartAt) { this.toastr.error('Select a start date'); return; }
    }

    this.savingAvailability = true;

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayDateOnly = `${yyyy}-${mm}-${dd}`;

    const payload = {
      availability: {
        start_at: this.availMode === 'now' ? `${todayDateOnly} 00:00:00.000` : `${this.availStartAt} 00:00:00.000`,
        duration_id: this.availMode === 'now' ? (this.availDurationId ?? null) : null
      }
    };

    this.dataService.updateUserAvailabilty(payload, headerValue).subscribe({
      next: (res: any) => {
        this.savingAvailability = false;
        this.preApply.availabilityOk = true;
        this.preApply.availabilityUpdatedAt = new Date().toISOString();
        this.preApply.availabilityTooltip = 'Last updated 0 day(s) ago';
        this.toastr.success(res?.data || 'Availability updated');
      },
      error: (err) => {
        this.savingAvailability = false;
        this.toastr.error(err?.error?.error?.description || 'Failed to update availability');
      }
    });
  }

  continueAfterFixes(): void {
    if (this.preApply.phoneOk && this.preApply.cvOk && this.preApply.availabilityOk) {
      this.modalService.dismissAll();
      this.openApplyConfirm();
    } else {
      this.toastr.warning('Please complete the required items before applying.');
    }
  }

  confirmApply(): void {
    if (this.post?.post_type !== 'job') return;
    if (this.isOwner || this.isRecruiter) return;
    const content = (this.applyMessage || '').trim() || '.';
    this.submitReply.emit({
      parentId: this.post.id,
      replyDepth: this.post.reply_depth,
      content,
      files: []
    });
    this.modalService.dismissAll();
  }

  cancelPreApply(): void { this.modalService.dismissAll(); }
  cancelApply(): void { this.modalService.dismissAll(); }

  onEditFileChange(event: any): void {
    const files = Array.from(event.target.files) as File[];
    const availableSlots = 5 - (this.existingAttachments.length + this.newEditImages.length);

    if (files.length > availableSlots) {
      this.toastr.error(`Only ${availableSlots} more image(s) can be added.`);
      return;
    }

    for (const file of files) {
      if (!file.type.startsWith('image/')) { this.toastr.error(`${file.name} is not an image.`); continue; }
      if (file.size > 5 * 1024 * 1024) { this.toastr.error(`${file.name} exceeds 5MB size limit`); continue; }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 4096 || img.height > 4096) { this.toastr.error(`${file.name} exceeds max resolution (4096x4096)`); return; }

          const finalize = (finalFile: File) => {
            this.newEditImages.push(finalFile);
            const previewReader = new FileReader();
            previewReader.onload = (ev: any) => { this.editImagePreviews.push(ev.target.result); this.cdRef.detectChanges(); };
            previewReader.readAsDataURL(finalFile);
          };

          if (img.width > 1024) {
            const canvas = document.createElement('canvas');
            const ratio = img.height / img.width;
            canvas.width = 1024; canvas.height = 1024 * ratio;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob((blob) => { if (blob) finalize(new File([blob], file.name, { type: file.type })); }, file.type);
          } else {
            finalize(file);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    event.target.value = '';
  }

  removeNewEditImage(index: number): void {
    this.newEditImages.splice(index, 1);
    this.editImagePreviews.splice(index, 1);
  }

  get canUseWebShare(): boolean {
    try {
      return typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';
    } catch {
      return false;
    }
  }
}
